// Part of settingsPages, split by screen to keep files short; ../settingsPages.ts merges the parts.
// Keys sit directly under settingsPages, in sub-objects whose names no other part uses.
// Move from QuickBooks (G7), the QuickBooks Online connection (G11), the import card, and the
// company settings rows no other part covers.
export const en = {
  qboHub: {
    description:
      'Bring a client over from QuickBooks Online at a conversion date: lists, opening balances and open items, then compare both systems month by month.',
    couldNotLoad: 'Could not load the checklist',
    stepsDone: '{{done}} of {{total}} steps done',
    finished: 'Finished',
    marked: '(marked)',
    compare: 'Compare',
    moveFinished: 'The move is finished',
    moreFor: 'More for {{step}}',
    markDone: 'Mark as done',
    skip: 'Skip (nothing to bring over)',
    conversionDate: 'Conversion date',
    conversionDateSaved: 'Conversion date saved',
    changeConversionDate: 'Change the conversion date',
    firstOfMonth: 'Use the first day of a month, such as 01/07/2026.',
    finishedToast: 'Finished. The books are locked through {{date}}.',
    tieOut: 'Tie-out',
    controlAccount: 'Control account',
    tieRow: { receivable: 'A/R (open invoices)', payable: 'A/P (unpaid bills)' },
    differs: '(differs)',
    openingEquity: 'Opening balance equity',
    openingEquityAt: 'Opening balance equity at {{date}}',
    finishLock: 'Finish and lock the books through {{date}}',
    needsCloseRights: 'Needs close rights for this client.',
    stepTitle: { review: 'Review and finish', parallel_run: 'Parallel run' },
    stepHint: {
      conversion:
        'The first day the books are kept here. Opening balances are dated the day before.',
      accounts: 'QuickBooks: Settings › Chart of accounts › Export.',
      customers: 'QuickBooks: Sales › Customers › Export.',
      vendors: 'QuickBooks: Expenses › Suppliers › Export.',
      items: 'QuickBooks: Sales › Products and services › Export.',
      opening_balances:
        'QuickBooks: Reports › Trial balance, as of the day before the conversion date.',
      open_invoices: 'QuickBooks: Reports › A/R ageing detail, as of the same date.',
      unpaid_bills: 'QuickBooks: Reports › A/P ageing detail, as of the same date.',
      review: 'Open items tie to the trial balance; the books are locked through the opening date.',
      parallel_run:
        'Compare QuickBooks and Outss Accounting month by month until the figures agree.',
    },
    todo: 'To do',
    side: {
      receivable: { control: 'A/R', report: 'A/R ageing detail', items: 'invoices and credits' },
      payable: { control: 'A/P', report: 'A/P ageing detail', items: 'bills and debit notes' },
    },
    uploadType: 'Choose the Excel (.xlsx) or CSV file exported from QuickBooks.',
    uploadSize: 'The file is larger than 3 MB.',
  },
  qboConnection: {
    title: 'QuickBooks Online connection',
    connected: 'Connected',
    notConnected: 'Not connected',
    quickBooksCompany: 'QuickBooks company',
    companyLine: '{{company}} · company ID {{realmId}}',
    sandboxTag: 'sandbox',
    connectedOn: 'Connected {{date}}',
    connectedOnBy: 'Connected {{date}} by {{name}}',
    tokenEnds: 'refresh token ends in {{hint}}',
    lastUsed: 'last used {{date}}',
    expiresIn_one: 'expires in {{count}} days unless used',
    expiresIn_other: 'expires in {{count}} days unless used',
    intro:
      "Connect the client's QuickBooks Online company to bring lists, opening balances and open items over with “Get from QuickBooks” instead of exporting files.",
    switchedOff: 'The connection is switched off on this server (no encryption key is set).',
    addKeysFirst: 'Add the QuickBooks app keys first.',
    adminMustAddKeys: 'An admin must add the QuickBooks app keys first.',
    appKeys: 'App keys',
    testing: 'Testing…',
    test: 'Test connection',
    confirmDisconnect:
      'Disconnect QuickBooks? The stored tokens are deleted; anything already imported stays.',
    disconnect: 'Disconnect',
    change: 'Change',
    connect: 'Connect',
    connectedTo: 'Connected to {{name}}',
    disconnected: 'Disconnected from QuickBooks',
    sheetTitle: 'Connect QuickBooks Online',
    sheetDescription:
      "The company (realm) ID and refresh token of this client's QuickBooks company (the same ones the client website uses). Saving checks them with QuickBooks. Tokens are stored encrypted and never shown again.",
    environment: 'Environment',
    environmentHint: 'Sandbox for a QuickBooks developer test company.',
    production: 'Production',
    sandbox: 'Sandbox',
    realmId: 'Company (realm) ID',
    refreshToken: 'Refresh token',
    refreshTokenHint:
      'Spaces and line breaks are removed. QuickBooks replaces it on every use; the newest is kept.',
    accessToken: 'Access token (optional)',
    accessTokenHint: 'Not needed: a new one is fetched with the refresh token.',
    companyName: 'Company name (optional)',
    companyNameHint: 'Read from QuickBooks when it answers.',
    checking: 'Checking with QuickBooks…',
    saveConnect: 'Save and connect',
    keysSaved: 'QuickBooks app keys saved',
    keysTitle: 'QuickBooks app keys',
    keysDescription:
      "The client ID and client secret of the practice's app on the Intuit developer site (Keys and credentials). Used for every client; the secret is stored encrypted and never shown again.",
    inUse: 'In use: client ID {{clientId}}, secret ending in {{hint}}.',
    inUseEnv:
      'In use: client ID {{clientId}}, secret ending in {{hint}} (from the server settings; saving here overrides them).',
    clientId: 'Client ID',
    clientSecret: 'Client secret',
    readRows_one: 'Read {{count}} row from {{company}}',
    readRows_other: 'Read {{count}} rows from {{company}}',
    readRowsAsOf_one: 'Read {{count}} row from {{company}} as of {{date}}',
    readRowsAsOf_other: 'Read {{count}} rows from {{company}} as of {{date}}',
    orConnect:
      'Or connect QuickBooks Online on <lnk>Move from QuickBooks</lnk> and read it from there.',
    getFromQuickBooks: 'Get from QuickBooks',
    companyRealm: 'company {{realmId}}',
    error: {
      enterRealm: 'Enter the company (realm) ID.',
      realmNumber: 'The company (realm) ID is a number.',
      enterRefresh: 'Enter the refresh token.',
      refreshShort: 'The refresh token looks too short.',
      accessShort: 'The access token looks too short.',
    },
  },
  qboImportCard: {
    description:
      'Bring invoices, bills, payments, expenses, deposits, transfers and journal entries over from QuickBooks, checked first and compared with QuickBooks afterwards.',
    last: '{{mode}}, {{from}} – {{to}}: {{imported}} imported.',
    lastFailed: '{{mode}}, {{from}} – {{to}}: {{imported}} imported, {{failed}} not imported.',
    history: 'History',
  },
  openingBalancesPosted: {
    postedAs:
      'Opening balances are posted as journal no. <lnk>{{entryNo}}</lnk>. Importing another file reverses that entry and posts the new one.',
    postedAsFrom:
      'Opening balances are posted as journal no. <lnk>{{entryNo}}</lnk> from {{fileName}}. Importing another file reverses that entry and posts the new one.',
    postedDone:
      '<b>Opening balances posted</b> as journal no. <lnk>{{entryNo}}</lnk>. The account mapping is saved for this client. Next, import the open invoices and unpaid bills.',
  },
  companyRows: {
    closeBooks:
      'Lock months on the Periods and close page (Accounting › <lnk>Periods and close</lnk>).',
  },
};

export const ar = {
  qboHub: {
    description:
      'انقل الشركة من QuickBooks Online في تاريخ تحويل: القوائم والأرصدة الافتتاحية والبنود المفتوحة، ثم قارن النظامين شهراً بشهر.',
    couldNotLoad: 'تعذّر تحميل قائمة الخطوات',
    stepsDone: 'اكتملت {{done}} من {{total}} خطوات',
    finished: 'منتهٍ',
    marked: '(مُعلَّمة يدوياً)',
    compare: 'قارن',
    moveFinished: 'اكتمل الانتقال',
    moreFor: 'المزيد لخطوة {{step}}',
    markDone: 'تعليم كمكتملة',
    skip: 'تخطٍّ (لا شيء لنقله)',
    conversionDate: 'تاريخ التحويل',
    conversionDateSaved: 'حُفظ تاريخ التحويل',
    changeConversionDate: 'تغيير تاريخ التحويل',
    firstOfMonth: 'استخدم اليوم الأول من الشهر، مثل 01/07/2026.',
    finishedToast: 'اكتمل الانتقال. الدفاتر مقفلة حتى {{date}}.',
    tieOut: 'المطابقة',
    controlAccount: 'الحساب الرقابي',
    tieRow: {
      receivable: 'الذمم المدينة (الفواتير المفتوحة)',
      payable: 'الذمم الدائنة (فواتير الموردين غير المسددة)',
    },
    differs: '(يوجد فرق)',
    openingEquity: 'حقوق ملكية الرصيد الافتتاحي',
    openingEquityAt: 'حقوق ملكية الرصيد الافتتاحي في {{date}}',
    finishLock: 'إنهاء وإقفال الدفاتر حتى {{date}}',
    needsCloseRights: 'يتطلب صلاحية الإقفال لهذه الشركة.',
    stepTitle: { review: 'المراجعة والإنهاء', parallel_run: 'التشغيل المتوازي' },
    stepHint: {
      conversion: 'أول يوم تُمسك فيه الدفاتر هنا. تُؤرَّخ الأرصدة الافتتاحية باليوم السابق له.',
      accounts: 'في QuickBooks: Settings › Chart of accounts › Export.',
      customers: 'في QuickBooks: Sales › Customers › Export.',
      vendors: 'في QuickBooks: Expenses › Suppliers › Export.',
      items: 'في QuickBooks: Sales › Products and services › Export.',
      opening_balances:
        'في QuickBooks: Reports › Trial balance، كما في اليوم السابق لتاريخ التحويل.',
      open_invoices: 'في QuickBooks: Reports › A/R ageing detail، كما في التاريخ نفسه.',
      unpaid_bills: 'في QuickBooks: Reports › A/P ageing detail، كما في التاريخ نفسه.',
      review: 'تتطابق البنود المفتوحة مع ميزان المراجعة، وتُقفل الدفاتر حتى تاريخ الافتتاح.',
      parallel_run: 'قارن QuickBooks وOutss Accounting شهراً بشهر حتى تتطابق الأرقام.',
    },
    todo: 'للتنفيذ',
    side: {
      receivable: {
        control: 'الذمم المدينة',
        report: 'تفاصيل أعمار الذمم المدينة',
        items: 'الفواتير والإشعارات الدائنة',
      },
      payable: {
        control: 'الذمم الدائنة',
        report: 'تفاصيل أعمار الذمم الدائنة',
        items: 'فواتير الموردين والإشعارات المدينة',
      },
    },
    uploadType: 'اختر ملف Excel ‏(.xlsx) أو CSV المُصدَّر من QuickBooks.',
    uploadSize: 'حجم الملف أكبر من 3 ميغابايت.',
  },
  qboConnection: {
    title: 'الربط مع QuickBooks Online',
    connected: 'مربوط',
    notConnected: 'غير مربوط',
    quickBooksCompany: 'شركة QuickBooks',
    companyLine: '{{company}} · رقم الشركة {{realmId}}',
    sandboxTag: 'بيئة تجريبية',
    connectedOn: 'رُبط في {{date}}',
    connectedOnBy: 'رُبط في {{date}} بواسطة {{name}}',
    tokenEnds: 'رمز التحديث ينتهي بـ {{hint}}',
    lastUsed: 'آخر استخدام {{date}}',
    expiresIn_zero: 'تنتهي صلاحيته اليوم ما لم يُستخدم',
    expiresIn_one: 'تنتهي صلاحيته خلال يوم واحد ما لم يُستخدم',
    expiresIn_two: 'تنتهي صلاحيته خلال يومين ما لم يُستخدم',
    expiresIn_few: 'تنتهي صلاحيته خلال {{count}} أيام ما لم يُستخدم',
    expiresIn_many: 'تنتهي صلاحيته خلال {{count}} يوماً ما لم يُستخدم',
    expiresIn_other: 'تنتهي صلاحيته خلال {{count}} يوم ما لم يُستخدم',
    intro:
      'اربط شركة العميل في QuickBooks Online لنقل القوائم والأرصدة الافتتاحية والبنود المفتوحة عبر «الجلب من QuickBooks» بدلاً من تصدير الملفات.',
    switchedOff: 'الربط معطّل على هذا الخادم (لم يُضبط مفتاح التشفير).',
    addKeysFirst: 'أضف مفاتيح تطبيق QuickBooks أولاً.',
    adminMustAddKeys: 'يجب أن يضيف المسؤول مفاتيح تطبيق QuickBooks أولاً.',
    appKeys: 'مفاتيح التطبيق',
    testing: 'جارٍ الاختبار…',
    test: 'اختبار الربط',
    confirmDisconnect: 'هل تريد فصل QuickBooks؟ تُحذف الرموز المخزّنة، ويبقى كل ما سبق استيراده.',
    disconnect: 'فصل',
    change: 'تغيير',
    connect: 'ربط',
    connectedTo: 'تم الربط مع {{name}}',
    disconnected: 'تم الفصل عن QuickBooks',
    sheetTitle: 'ربط QuickBooks Online',
    sheetDescription:
      'رقم الشركة (realm ID) ورمز التحديث لشركة هذا العميل في QuickBooks (نفس ما يستخدمه موقع العميل). يتحقق الحفظ منهما مع QuickBooks. تُخزَّن الرموز مشفّرة ولا تُعرض مرة أخرى.',
    environment: 'البيئة',
    environmentHint: 'البيئة التجريبية لشركة اختبار مطوّري QuickBooks.',
    production: 'الإنتاج',
    sandbox: 'تجريبية',
    realmId: 'رقم الشركة (realm ID)',
    refreshToken: 'رمز التحديث',
    refreshTokenHint:
      'تُزال المسافات وفواصل الأسطر. يستبدله QuickBooks عند كل استخدام، ويُحتفظ بأحدثه.',
    accessToken: 'رمز الوصول (اختياري)',
    accessTokenHint: 'غير مطلوب: يُجلب رمز جديد باستخدام رمز التحديث.',
    companyName: 'اسم الشركة (اختياري)',
    companyNameHint: 'يُقرأ من QuickBooks عند استجابته.',
    checking: 'جارٍ التحقق مع QuickBooks…',
    saveConnect: 'حفظ وربط',
    keysSaved: 'حُفظت مفاتيح تطبيق QuickBooks',
    keysTitle: 'مفاتيح تطبيق QuickBooks',
    keysDescription:
      'معرّف العميل وكلمة سر العميل لتطبيق المكتب على موقع مطوّري Intuit ‏(Keys and credentials). تُستخدم لجميع العملاء، وتُخزَّن كلمة السر مشفّرة ولا تُعرض مرة أخرى.',
    inUse: 'المستخدم حالياً: معرّف العميل {{clientId}}، وكلمة سر تنتهي بـ {{hint}}.',
    inUseEnv:
      'المستخدم حالياً: معرّف العميل {{clientId}}، وكلمة سر تنتهي بـ {{hint}} (من إعدادات الخادم، والحفظ هنا يتجاوزها).',
    clientId: 'معرّف العميل (Client ID)',
    clientSecret: 'كلمة سر العميل (Client secret)',
    readRows_zero: 'لم يُقرأ أي صف من {{company}}',
    readRows_one: 'قُرئ صف واحد من {{company}}',
    readRows_two: 'قُرئ صفان من {{company}}',
    readRows_few: 'قُرئت {{count}} صفوف من {{company}}',
    readRows_many: 'قُرئ {{count}} صفاً من {{company}}',
    readRows_other: 'قُرئ {{count}} صف من {{company}}',
    readRowsAsOf_zero: 'لم يُقرأ أي صف من {{company}} كما في {{date}}',
    readRowsAsOf_one: 'قُرئ صف واحد من {{company}} كما في {{date}}',
    readRowsAsOf_two: 'قُرئ صفان من {{company}} كما في {{date}}',
    readRowsAsOf_few: 'قُرئت {{count}} صفوف من {{company}} كما في {{date}}',
    readRowsAsOf_many: 'قُرئ {{count}} صفاً من {{company}} كما في {{date}}',
    readRowsAsOf_other: 'قُرئ {{count}} صف من {{company}} كما في {{date}}',
    orConnect:
      'أو اربط QuickBooks Online من صفحة <lnk>الانتقال من QuickBooks</lnk> واقرأ البيانات من هناك.',
    getFromQuickBooks: 'الجلب من QuickBooks',
    companyRealm: 'الشركة {{realmId}}',
    error: {
      enterRealm: 'أدخل رقم الشركة (realm ID).',
      realmNumber: 'رقم الشركة (realm ID) يتكوّن من أرقام فقط.',
      enterRefresh: 'أدخل رمز التحديث.',
      refreshShort: 'يبدو رمز التحديث قصيراً جداً.',
      accessShort: 'يبدو رمز الوصول قصيراً جداً.',
    },
  },
  qboImportCard: {
    description:
      'انقل الفواتير وفواتير الموردين والدفعات والمصروفات والإيداعات والتحويلات وقيود اليومية من QuickBooks، مع فحصها أولاً ومقارنتها مع QuickBooks بعد ذلك.',
    last: '{{mode}}، {{from}} – {{to}}: استُورد {{imported}}.',
    lastFailed: '{{mode}}، {{from}} – {{to}}: استُورد {{imported}}، ولم يُستورد {{failed}}.',
    history: 'السجل',
  },
  openingBalancesPosted: {
    postedAs:
      'رُحّلت الأرصدة الافتتاحية في قيد اليومية رقم <lnk>{{entryNo}}</lnk>. يؤدي استيراد ملف آخر إلى عكس هذا القيد وترحيل القيد الجديد.',
    postedAsFrom:
      'رُحّلت الأرصدة الافتتاحية في قيد اليومية رقم <lnk>{{entryNo}}</lnk> من الملف {{fileName}}. يؤدي استيراد ملف آخر إلى عكس هذا القيد وترحيل القيد الجديد.',
    postedDone:
      '<b>رُحّلت الأرصدة الافتتاحية</b> في قيد اليومية رقم <lnk>{{entryNo}}</lnk>. حُفظ ربط الحسابات لهذه الشركة. الخطوة التالية: استيراد الفواتير المفتوحة والفواتير غير المدفوعة.',
  },
  companyRows: {
    closeBooks: 'أقفل الأشهر من صفحة الفترات والإقفال (المحاسبة › <lnk>الفترات والإقفال</lnk>).',
  },
};
